/**
 * Utility functions to send messages between:
 * - Extension content script
 * - Extension background page
 * - embeddedWebReader.js or importerBookmarklet.js (running within 3rd party website)
 * - bookmarklet.js or bookmarkletAddDocument.js (running within readlang.com iframe)
 */
const readlangMessageType = "readlangMessage";

const verboseMode = false;

interface IFrameTarget {
  window: any;
  /** Origin the iFrame must still be on for a message to be delivered. */
  origin: string;
}

/**
 * These will be set by the embeddedWebReader / importerBookmarklet with a handle to the iFrame,
 * allowing us to send messages to it.
 */
let webReaderIFrame: IFrameTarget | undefined;
let importerIFrame: IFrameTarget | undefined;

type Context =
  // Chrome extension
  | "extension"

  // Readlang script running in 3rd party website
  // XXX Rename to "embeddedScript" since it also applies to importerBookmarklet??
  | "embeddedWebReader"

  // Readlang script running in readlang.com iframe
  | "webReaderIFrame"

  // Readlang script running in readlang.com iframe
  | "importerIFrame";

/**
 * @param to - Which context to send the message to
 * @param message - The message
 * @param data - Optional data, will be JSON stringified
 * @param requestId - Optional, only relevant for extension context, used to match request/response
 */
export const postMessage = (
  to: Context,
  message: string,
  data?: object,
  requestId?: string
) => {
  if (verboseMode) {
    console.log(`Posting message to ${to}: ${message}`);
  }

  // In the iOS app's browser the Web Reader's toolbar is a native panel over
  // the page rather than an iFrame in it, so the app carries their messages.
  const nativeToolbar = window.READLANG?.nativeToolbar;
  if (
    nativeToolbar &&
    (to === "embeddedWebReader" || to === "webReaderIFrame")
  ) {
    nativeToolbar.post(
      JSON.stringify({ type: readlangMessageType, to, message, data })
    );
    return;
  }

  switch (to) {
    case "extension":
      // Picked up by the extension's content script, which is injected into this
      // same window. Keeping the target origin exact means no other window ever
      // sees the message, however this page came to be embedded.
      window.postMessage(
        JSON.stringify({
          type: readlangMessageType,
          to,
          message: "backgroundPageMessage",
          requestId,
          data: {
            message,
            data,
          },
        }),
        window.location.origin
      );
      break;

    case "embeddedWebReader":
      // The web reader is embedded on arbitrary sites, so the parent's origin is
      // unknown here and "*" is unavoidable. The receiving side checks that
      // messages came from Readlang.
      top.postMessage(
        JSON.stringify({
          type: readlangMessageType,
          to,
          message,
          data,
        }),
        "*"
      );
      break;

    case "webReaderIFrame":
      if (!webReaderIFrame) {
        throw Error("web reader iFrameWindow not set");
      }

      postMessageToIFrame(webReaderIFrame, to, message, data);
      break;

    case "importerIFrame":
      if (!importerIFrame) {
        throw Error("importer iFrameWindow not set");
      }

      postMessageToIFrame(importerIFrame, to, message, data);
      break;
  }
};

/**
 * Messages to our iFrames can carry the page's content, so they are addressed to
 * the Readlang origin the iFrame was loaded from: if it has since navigated
 * elsewhere the browser drops the message rather than handing it over.
 */
const postMessageToIFrame = (
  target: IFrameTarget,
  to: Context,
  message: string,
  data?: object
) => {
  target.window.postMessage(
    JSON.stringify({
      type: readlangMessageType,
      to,
      message,
      data,
    }),
    target.origin
  );
};

export const setWebReaderIFrame = (newIFrameWindow: any, origin: string) => {
  webReaderIFrame = { window: newIFrameWindow, origin };
};

export const setImporterIFrame = (newIFrameWindow: any, origin: string) => {
  importerIFrame = { window: newIFrameWindow, origin };
};

// eslint-disable-next-line no-unused-vars
type MessageHandler = (message: string, data: any, rawEvent?: any) => void;

const eventListeners = new Map<string, any>();
const nativeListeners = new Set<(event: MessageEvent) => void>();

export const messageListener = (context: Context, handler: MessageHandler) => {
  if (!("addEventListener" in window)) {
    alert(
      "The Readlang bookmarklet is not working on this page using this browser." +
        "\n\nTry using Google Chrome instead."
    );
    return;
  }

  const listener = (event: MessageEvent) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (_error) {
      // Ignore non-JSON messages.
      return;
    }

    if (data?.type !== readlangMessageType) {
      // console.log("Ignoring invalid message", event.data);
      return;
    }

    if (data.to !== context) {
      // console.log(
      //   `Ignoring message for different context. ${data.to} !== ${context}`
      // );
      return;
    }

    if (context === "extension") {
      throw Error("Extension requests handled by code in contentScript.js");
    }

    if (verboseMode) {
      console.log(
        `Received message to ${context}: ${data.message} (from ${event.origin})`
      );
    }

    handler(data.message, data.data, event);
  };

  // In the iOS app's browser the app delivers the other side's messages
  // itself, into the Web Reader's isolated world: the page's scripts, which
  // can dispatch DOM message events claiming any origin, can't reach it.
  const nativeToolbar = window.READLANG?.nativeToolbar;
  if (nativeToolbar) {
    nativeListeners.add(listener);
    nativeToolbar.receive = (json, origin) => {
      for (const nativeListener of nativeListeners) {
        nativeListener({ data: json, origin } as MessageEvent);
      }
    };
  } else {
    window.addEventListener("message", listener);
  }

  eventListeners.set(context, listener);
};

export const removeMessageListener = (context: string) => {
  const listener = eventListeners.get(context);
  window.removeEventListener("message", listener);
  nativeListeners.delete(listener);
  eventListeners.delete(context);
};
